/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Button from '@mui/material/Button'
import { debounce } from 'lodash'
import Input from 'components/Input'
import { DEBOUNCE_DELAY } from 'constants/consts'
import { ReactComponent as ChevronLeftIcon } from 'assets/icons/chevron-left.svg'
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevron-right.svg'
import { FIRST_PAGE } from 'hooks/useQueryWithParams'
import * as classes from './styles'

export type PaginationPageProps = {
  totalCount?: number
  count: number
  page: number
  onChange: (page: number) => void
  className?: string
}

export const PaginationPage = ({ totalCount, count, page, onChange, className }: PaginationPageProps) => {
  const [pageNumber, setPageNumber] = useState(page)
  const [inputValue, setInputValue] = useState(String(page))
  const inputNumber = Math.round(+inputValue)
  const isInvalid = useMemo(
    () => isNaN(+inputValue) || !inputValue.match(/^\d*$/) || inputNumber < FIRST_PAGE || inputNumber > count,
    [inputValue, count]
  )

  useEffect(() => {
    setPageNumber(page)
  }, [page])

  useEffect(() => {
    setInputValue(String(pageNumber))
  }, [pageNumber])

  const debouncedOnChange = useCallback(debounce(onChange, DEBOUNCE_DELAY), [onChange])

  const handleClick = useCallback(
    (diff: number) => () => {
      setPageNumber((pageNumber) => {
        const newPage = Math.max(pageNumber + diff, FIRST_PAGE)
        if (newPage !== page) {
          debouncedOnChange(newPage)
        }
        return newPage
      })
    },
    [page, debouncedOnChange]
  )

  const handleBlur = useCallback(() => {
    if (isInvalid || inputNumber === page) {
      return
    }

    debouncedOnChange(inputNumber)
  }, [page, isInvalid, inputNumber, debouncedOnChange])

  return (
    <div css={classes.pageWrapper} className={className}>
      {totalCount !== undefined && <span>{totalCount} items</span>}
      <Button
        size="large"
        color="blue"
        variant="outlined"
        onClick={handleClick(-1)}
        disabled={pageNumber <= FIRST_PAGE}
      >
        <ChevronLeftIcon />
      </Button>
      <Input
        value={inputValue}
        onChange={setInputValue}
        onBlur={handleBlur}
        error={isInvalid && count > 0}
        placeholder={String(page)}
      />
      <Button size="large" color="blue" variant="outlined" onClick={handleClick(1)} disabled={pageNumber >= count}>
        <ChevronRightIcon />
      </Button>
      <span>of {count ?? 0}</span>
    </div>
  )
}
